import React, { useContext } from "react";
import {
  Box,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormGroup,
  Grid,
  IconButton,
  TextField,
  Typography,
} from "@mui/material";
import { Formik, FormikHelpers } from "formik";
import { ScreenFlowFormButtons } from "./ScreenFlowFormButtons";
import HelpOutlineOutlinedIcon from "@mui/icons-material/HelpOutlineOutlined";
import { AppContext } from "../../../App";
import { HelpContent } from "../../shared/help";
import { objectVals2String } from "../../../utils/objectVals2String";

interface Values {
  checked: string[];
  checkedFollowUps: string[];
}

export type CheckOption = {
  key: string;
  value: string;
  label?: string;
  response?: string | JSX.Element;
  help?: HelpContent;
  followUp?: string;
};

export type followUpsCharCount = {
  charCount: number;
  setCharCount: any;
};

interface Props {
  initChecked?: string[];
  initFollowUps?: string[];
  followUpsCharLimit?: number;
  followUpsRows?: number;
  answerLabel?: string;
  options: CheckOption[];
  onComplete: (
    checked: string[],
    checkedFollowUps: string[]
  ) => Promise<boolean>;
  onBack?: () => void;
  ctaText?: string;
}

export const MultiCheckForm = ({
  initChecked,
  initFollowUps,
  followUpsCharLimit,
  followUpsRows,
  options,
  onComplete,
  onBack,
  ctaText,
}: Props): JSX.Element => {
  const { setHelpContent } = useContext(AppContext);

  // https://formik.org/docs/api/formik
  const formikInit = {
    values: {
      checked: initChecked || [""],
      checkedFollowUps: initFollowUps || [""],
    },
  };

  const followUpsCharCounts: followUpsCharCount[] = [];
  if (initFollowUps && initFollowUps.length > 0 && followUpsCharLimit) {
    for (const followUp of initFollowUps) {
      const [charCount, setCharCount] = React.useState(followUp.length);
      followUpsCharCounts.push({
        charCount: charCount,
        setCharCount: setCharCount,
      });
    }
  }
  const handleSubmit = async (
    values: Values,
    { setSubmitting, setStatus }: FormikHelpers<Values>
  ) => {
    setStatus("");
    try {
      // console.log(values.checked);
      if (values.checked[0] === "") {
        values.checked.shift();
      }
      // console.log(values.checkedFollowUps);

      // manually validate checkedFollowUps
      options.forEach((option: CheckOption, index: number) => {
        if (option?.followUp) {
          // has follow up
          if (values.checked.includes(option.key)) {
            // option is checked
            if (!values.checkedFollowUps[index]) {
              // and the follow up is empty
              throw Error(`"${option?.followUp}" is required`);
            }
            if (
              followUpsCharLimit &&
              values.checkedFollowUps[index].length > followUpsCharLimit
            ) {
              throw Error(
                `"${option?.value} -> ${option?.followUp}" must be ${followUpsCharLimit} characters or less`
              );
            }
          } else {
            // option is not checked
            if (values.checkedFollowUps[index] !== "") {
              // follow up is not blank, clear it
              values.checkedFollowUps[index] = "";
            }
          }
        }
      });

      await onComplete(values.checked, values.checkedFollowUps);
    } catch (error) {
      setStatus((error as Error)?.message || "");
      setSubmitting(false);
    }
  };

  return (
    <Formik
      initialValues={formikInit.values}
      onSubmit={handleSubmit}
      key={objectVals2String(formikInit.values)}
      enableReinitialize
    >
      {({
        errors,
        handleChange,
        handleSubmit,
        isSubmitting,
        values,
        status,
        setStatus,
        setFieldValue,
      }) => (
        <form onSubmit={handleSubmit}>
          <Grid
            container
            spacing={1}
            sx={{
              my: 1,
            }}
          >
            <Grid item xs={12}>
              <Box sx={{ display: "flex" }}>
                <FormControl
                  sx={{ width: "100%" }}
                  component="fieldset"
                  variant="standard"
                >
                  <FormGroup>
                    {options?.map((option: CheckOption, index: number) => (
                      <Box key={`check-${option.key}`}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={values.checked.includes(option.key)}
                              value={option.key}
                              onChange={(evt) => {
                                setStatus(""); // clear error
                                handleChange(evt);

                                // Clear character count, if follow up exists
                                if (option.followUp && followUpsCharLimit) {
                                  followUpsCharCounts[index].setCharCount(0);
                                }
                              }}
                              name="checked"
                            />
                          }
                          label={option.label || option.value}
                          sx={{
                            my: "4px",
                          }}
                        />
                        {option.help && (
                          <IconButton
                            onClick={() => {
                              option.help && setHelpContent(option.help);
                            }}
                            aria-label="help"
                            color="secondary"
                          >
                            <HelpOutlineOutlinedIcon />
                          </IconButton>
                        )}
                        {values.checked.includes(option.key) &&
                          option.followUp && (
                            <>
                              <br />
                              <TextField
                                multiline={true}
                                rows={followUpsRows ? followUpsRows : 3}
                                fullWidth={true}
                                label={option.followUp}
                                helperText={
                                  followUpsCharLimit
                                    ? `${
                                        followUpsCharLimit -
                                        followUpsCharCounts[index].charCount
                                      } characters left`
                                    : ""
                                }
                                onChange={(event) => {
                                  if (option.followUp && followUpsCharLimit) {
                                    followUpsCharCounts[index].setCharCount(
                                      event.target.value.length
                                    );
                                  }
                                }}
                                error={
                                  followUpsCharLimit &&
                                  followUpsCharCounts[index].charCount >
                                    followUpsCharLimit
                                    ? true
                                    : false
                                }
                                // since this is not a controlled component, this will be used to pre-populate value from db
                                defaultValue={
                                  (initFollowUps && initFollowUps[index]) ||
                                  undefined
                                }
                                name={`checkedFollowUps${index}`}
                                onBlur={(evt: React.FocusEvent<any>) => {
                                  // manually set values.checkedFollowUps[index]
                                  const followUpValues = [
                                    ...values.checkedFollowUps,
                                  ]; // clone the array
                                  followUpValues[index] =
                                    evt.target.value?.trim();
                                  setFieldValue(
                                    "checkedFollowUps",
                                    followUpValues
                                  );
                                }}
                                type="text"
                                variant="outlined"
                                sx={{
                                  my: 2,
                                }}
                              />
                            </>
                          )}
                      </Box>
                    ))}
                  </FormGroup>
                </FormControl>
              </Box>
              <Typography variant="body1" color="error">
                {errors.checked}
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <ScreenFlowFormButtons
                isSubmitting={isSubmitting}
                status={status}
                onBack={onBack}
                text={ctaText}
              />
            </Grid>
          </Grid>
        </form>
      )}
    </Formik>
  );
};
